import { ObservationControllerService } from "@9amhealth/openapi";
import dayjs from "dayjs";
import type {
  Bundle,
  BundleEntry,
  CodeableConcept,
  DiagnosticReport,
  Observation,
  ObservationReferenceRange
} from "fhir/r4";
import { ObservationCategory } from "src/constants/ObservationCategory";
import type { ObservationSource } from "src/constants/ObservationSource";
import { LoincCodingCode } from "src/constants/fhir";
import {
  getObservationRangesData as computeObservationRangesData,
  getObservationData as computeObservationData,
  getObservationTitle as computeObservationTitle,
  INTERPRETATION_DISPLAY_ORDER,
  INTERPRETATION_EVALUATION_ORDER
} from "@9amhealth/shared";
import { dateLocalString } from "src/lib/date";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { toast } from "src/state/state";
import LoadingCubit, { LoadingKey } from "../LoadingCubit/LoadingCubit";
import { globalEvents } from "src/constants/globalEvents";
import { Blac, Cubit } from "blac-next";
import type { ObservationBundle } from "src/types/observationLaboratory";
import { ObservationBundleCategory } from "src/types/observationLaboratory";
import {
  getBundleSourceDisplayName,
  resolveObservationBundleCategory
} from "./resolveObservationBundleCategory";
import { resolveObservationBundleLabelKey } from "./resolveObservationBundleLabel";
import { getBundleCollectedDate } from "./getBundleCollectedDate";
import { resolveObservationBundleIcon } from "./resolveObservationBundleIcon";
import { resolveObservationBundleSubtitle } from "./resolveObservationBundleSubtitle";

export interface CustomObservationReferenceRange extends ObservationReferenceRange {
  interpretationCode?: CodeableConcept;
  id?: string;
  code?: string;
  min?: number;
  max?: number;
  atLeastOneLimit?: boolean;
  onlyOneLimit?: boolean;
  widthPercentage?: number;
  cursorPositionPercentageFromLeft?: number;
}

export interface ObservationBundleSearchParams {
  observationDate?: string;
  category?: ObservationCategory;
  source?: ObservationSource;
  labOrderId?: string;
}

interface ObservationRangesData {
  observationValue?: number;
  ranges?: CustomObservationReferenceRange[];
  normalRange?: CustomObservationReferenceRange;
  normalRangeMin?: number;
  normalRangeMax?: number;
  totalWidth?: number;
}

interface ObservationBundleState {
  observationBundles?: Bundle<Observation>[];
  observationBundle?: Bundle<DiagnosticReport | Observation>;
  observations?: BundleEntry<Observation>[];
}

export default class ObservationBundleBloc extends Cubit<ObservationBundleState> {
  static keepAlive = true;

  constructor() {
    super({
      observationBundles: undefined,
      observationBundle: undefined,
      observations: undefined
    });
    window.addEventListener(globalEvents.USER_CLEAR, () => {
      this.emit({
        observationBundles: undefined,
        observationBundle: undefined,
        observations: undefined
      });
    });
  }

  public readonly loadObservationBundles = async (
    force = false
  ): Promise<void> => {
    if (!force && this.state.observationBundles) {
      return;
    }
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.loadingObservationBundles);
    try {
      const response = await ObservationControllerService.getObservationBundles(
        [ObservationCategory.LABORATORY]
      );

      const { data } = response;

      // Sort in descending chronological order
      data.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return dayjs(a.timestamp).isAfter(b.timestamp) ? -1 : 1;
        }

        return 1;
      });

      this.emit({
        ...this.state,
        observationBundles: data
      });
    } catch (e: unknown) {
      reportErrorSentry(e);
    } finally {
      loadingState.finish(LoadingKey.loadingObservationBundles);
    }
  };

  public readonly loadObservationBundle = async (
    searchParams: ObservationBundleSearchParams,
    force = false
  ): Promise<void> => {
    if (!force && this.state.observationBundle) {
      return;
    }
    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.loadingObservationBundle);
    try {
      const response = await ObservationControllerService.getBundleByParams(
        {},
        searchParams.observationDate,
        searchParams.category,
        searchParams.source,
        searchParams.labOrderId
      );

      // TODO: Update type if BE updates it
      const { data } = response as unknown as {
        status: string;
        data: Bundle<DiagnosticReport | Observation>;
      };

      this.emit({
        ...this.state,
        observationBundle: data
      });
    } catch (e: unknown) {
      reportErrorSentry(e);

      toast.error("error.generic");
    } finally {
      loadingState.finish(LoadingKey.loadingObservationBundle);
    }
  };

  public readonly getObservationsFromBundle = (
    observationBundle?: Bundle<DiagnosticReport | Observation>
  ) => {
    const hiddenObservationCodes: LoincCodingCode[] = [
      LoincCodingCode.bmi,
      LoincCodingCode.height,
      LoincCodingCode.weight,
      LoincCodingCode.stepsInDay
    ];

    // Filter out  diagnostic report and bmi, height, and weight observations
    const filteredObservations = observationBundle?.entry?.filter((obs) => {
      return (
        obs.resource?.resourceType === "Observation" &&
        !hiddenObservationCodes.includes(
          obs.resource.code.coding?.[0].code as LoincCodingCode
        )
      );
    }) as BundleEntry<Observation>[] | undefined;

    // Sort observations alphabetically
    filteredObservations?.sort((a, b) =>
      (a.resource?.code.coding?.[0].display ?? "").localeCompare(
        b.resource?.code.coding?.[0].display ?? ""
      )
    );

    this.emit({
      ...this.state,
      observations: filteredObservations
    });
  };

  public readonly getObservationBundleLabel = (
    bundle: Bundle<Observation>
  ): string | null => {
    const category = resolveObservationBundleCategory(
      bundle as unknown as ObservationBundle
    );
    const key = resolveObservationBundleLabelKey(category);

    return key === null ? null : translate(key);
  };

  public readonly getObservationBundleCategory = (
    bundle: Bundle<DiagnosticReport | Observation>
  ): ObservationBundleCategory =>
    resolveObservationBundleCategory(bundle as unknown as ObservationBundle);

  public readonly getObservationBundleSourceName = (
    bundle: Bundle<DiagnosticReport | Observation>
  ): string | undefined =>
    getBundleSourceDisplayName(bundle as unknown as ObservationBundle);

  /**
   * Resolves a single list row from a bundle. The backend emits one single-source
   * bundle per source (a day spanning several sources yields several bundles), so
   * each bundle maps directly to one row (icon / label / source name).
   */
  public readonly getBundleData = (bundle: Bundle<Observation>) => {
    const observationBundle = bundle as unknown as ObservationBundle;
    const baseQuery = bundle.link?.[0]?.url?.split("?")[1] ?? "";
    const params = new URLSearchParams(baseQuery);
    const link = `/app/lab-results/bundle?${params.toString()}`;
    const category = resolveObservationBundleCategory(observationBundle);
    const Icon = resolveObservationBundleIcon(observationBundle);
    const key = resolveObservationBundleLabelKey(category);
    const label = key === null ? null : translate(key);
    const collected = getBundleCollectedDate(observationBundle);
    const sortDate = collected ?? bundle.timestamp;
    const date = dateLocalString(sortDate, {
      dateStyle: "medium"
    });
    // HIE imports and patient file uploads prefix a source phrase; care-team
    // uploads and everything else show just the date.
    const subtitle = resolveObservationBundleSubtitle(
      category,
      date,
      observationBundle
    );

    return { link, label, date, subtitle, sortDate, category, Icon };
  };

  /**
   * Newest-first ordering value for a row. Undated rows (no collected date and
   * no timestamp) return -Infinity so they sort deterministically to the bottom
   * rather than to the top — dayjs(undefined) would resolve to "now" and float
   * undated bundles above real ones with an empty subtitle.
   */
  private readonly bundleSortValue = (row: { sortDate?: string }): number =>
    row.sortDate ? dayjs(row.sortDate).valueOf() : Number.NEGATIVE_INFINITY;

  /**
   * Rows to render, in display order. Filters out EXCLUDED bundles, then sorts
   * newest-first. `limit` is applied *after* filtering so hidden bundles never
   * consume visible slots.
   */
  public readonly getVisibleBundleData = (
    bundles: Bundle<Observation>[],
    limit?: number
  ) => {
    const visible = bundles
      .map((bundle) => this.getBundleData(bundle))
      .filter((data) => data.category !== ObservationBundleCategory.EXCLUDED)
      // Re-sort by the row's shown collected date, newest first. The upstream
      // bundle sort is by `timestamp`, which can differ from the displayed
      // collected date, so re-sort here to keep the visible order consistent
      // with the visible dates.
      .sort((a, b) => this.bundleSortValue(b) - this.bundleSortValue(a));

    return typeof limit === "number" ? visible.slice(0, limit) : visible;
  };

  public readonly hasImportedBundle = (
    bundles: Bundle<Observation>[]
  ): boolean =>
    bundles.some(
      (bundle) =>
        resolveObservationBundleCategory(
          bundle as unknown as ObservationBundle
        ) === ObservationBundleCategory.IMPORTED
    );

  public readonly getRangeContainingObservation = (
    observation: Observation
  ): CustomObservationReferenceRange | undefined => {
    const observationValue = observation.valueQuantity?.value;
    const observationRanges = observation.contained as
      | CustomObservationReferenceRange[]
      | undefined;

    if (typeof observationValue !== "number" || !observationRanges?.length) {
      return undefined;
    }

    // Get decorated ranges from shared package
    const rangesData = computeObservationRangesData(observation);
    const decoratedRanges = rangesData.ranges;

    if (!decoratedRanges?.length) {
      return undefined;
    }

    // Sort ranges using evaluation order (inside-out checking logic)
    const sortedObservationRanges = this.sortObservationRangesByCode(
      decoratedRanges,
      INTERPRETATION_EVALUATION_ORDER
    );

    return sortedObservationRanges.find((range) => {
      const min = range.low?.value;
      const max = range.high?.value;

      if (typeof min === "number" && typeof max === "number") {
        if (observationValue >= min && observationValue <= max) {
          return true;
        }
      } else if (
        (typeof min === "number" && observationValue >= min) ||
        (typeof max === "number" && observationValue <= max)
      ) {
        return true;
      }

      return false;
    });
  };

  public readonly getObservationRangesData = (
    observation: Observation
  ): ObservationRangesData => {
    const data = computeObservationRangesData(observation);
    const normalRange = data.ranges?.find((range) => range.code === "N");
    return {
      observationValue: data.observationValue,
      ranges: data.ranges,
      normalRange,
      normalRangeMin: data.normalRangeMin,
      normalRangeMax: data.normalRangeMax,
      totalWidth: data.totalWidth
    };
  };

  public readonly getObservationTitle = (
    observation: Observation
  ): string | undefined => {
    const title = computeObservationTitle(observation);
    return typeof title === "string" ? title : undefined;
  };

  public readonly getObservationData = (observation: Observation) => {
    const sharedData = computeObservationData(observation);
    // Get decorated ranges from shared package
    const rangesData = computeObservationRangesData(observation);
    const decoratedRanges = rangesData.ranges;
    return {
      observationValueString: sharedData.observationValueString,
      observationValue: sharedData.observationValue,
      observationUnit: sharedData.observationUnit,
      observationTitle: sharedData.observationTitle,
      ranges:
        decoratedRanges && decoratedRanges.length > 0
          ? decoratedRanges
          : undefined,
      isRangeDefined: sharedData.isRangeDefined
    };
  };

  public readonly sortObservationRangesByCode = (
    ranges: CustomObservationReferenceRange[],
    codeOrder: readonly string[] = INTERPRETATION_DISPLAY_ORDER
  ) => {
    return [...ranges].sort((a, b) => {
      const codeA = a.interpretationCode?.coding?.[0].code;
      const codeB = b.interpretationCode?.coding?.[0].code;
      const codeAIndex = codeOrder.indexOf(codeA ?? "");
      const codeBIndex = codeOrder.indexOf(codeB ?? "");

      return codeAIndex < codeBIndex ? -1 : 1;
    });
  };

  //#region Reference range bar helpers (used mainly in ReferenceRangeBar.tsx)
  public readonly getCursorPositionPercentageFromLeft = (
    range: CustomObservationReferenceRange,
    value?: number
  ): number => {
    const min = range.low?.value;
    const max = range.high?.value;
    let percentage = 50;

    if (
      typeof value === "number" &&
      typeof min === "number" &&
      typeof max === "number"
    ) {
      percentage = ((value - min) / (max - min)) * 100;
    }

    return percentage;
  };

  public readonly getRangeWidthPercentage = (
    range?: CustomObservationReferenceRange,
    totalWidth?: number,
    onlyNormalRange: boolean = false
  ): number => {
    const rangeMin = range?.low?.value;
    const rangeMax = range?.high?.value;
    let percentage = 0;

    if (typeof rangeMin === "number" && typeof rangeMax === "number") {
      if (onlyNormalRange) {
        percentage = 50;
      } else {
        const rangeWidth = rangeMax - rangeMin;
        if (totalWidth) {
          percentage = (rangeWidth * 100) / totalWidth;
        }
      }
    } else if (typeof rangeMin === "number" || typeof rangeMax === "number") {
      percentage = 75;
    }

    if (percentage < 0) {
      percentage = 0;
    }
    if (percentage > 100) {
      percentage = 100;
    }

    return percentage;
  };
  //#endregion
}
